import type { OpenInterval } from "@suse-o11y/date-time/open-interval";
import type { Time } from "@suse-o11y/date-time/time";
import { type Signal, useMonitor } from "@suse-o11y/signal";
import React from "react";

import { Permission } from "~/models/dto";

import { SpecificRange, type TimeRange } from "../../app/state/view/timeline/TimeRange";
import type { MonitorCheckStatusHealthChange } from "../../common/openapi";
import { usePermissions } from "../../common/use-permissions";
import { Timeline, TimelineHealthState, TimelineMutedState, TimelineTimeTicks } from "../_timeline";

interface HealthTimelineChartProps {
	disabled?: boolean;
	timestamp: Signal<undefined | Time>;
	timeRange: Signal<TimeRange>;
	healthStateChanges: undefined | ReadonlyArray<MonitorCheckStatusHealthChange>;
	muteIntervals: undefined | ReadonlyArray<OpenInterval>;
}

export const HealthTimelineChart: React.FC<HealthTimelineChartProps> = React.memo(
	({ disabled, timestamp, timeRange, healthStateChanges, muteIntervals }) => {
		const monitor = useMonitor();
		const permissions = usePermissions();

		return (
			<Timeline
				brush={{
					areaOffsetTop: 20,
					areaOffsetBottom: 5,
					onBrush: (left, right) => timeRange.write(new SpecificRange(left, right)),
				}}
				data-testid="health-timeline-chart"
				disabled={disabled || healthStateChanges == null}
				height={50}
				hideTopologyTimeMarker
				onClick={(time) => timestamp.write(time)}
				timeRange={timeRange.read(monitor)}
				timestamp={timestamp.read(monitor)}
			>
				<TimelineTimeTicks withTicks y={14} />

				{permissions?.hasSystemPermission(Permission.GetSilencingRules) && (
					<TimelineMutedState muteIntervals={muteIntervals ?? []} y={32} />
				)}

				<TimelineHealthState health={healthStateChanges} y={32} />
			</Timeline>
		);
	},
);
